/**
 * Platform-wide documentation registry.
 *
 * To add a new doc:
 *   1. Author the Markdown as `frontend/src/docs/<slug>.{en,fr,es}.js`
 *      (each file `export default <content-string>`).
 *   2. Register the doc below.
 *
 * The `id` is the stable slug used by `<HelpButton docId="…" />`.
 * `title` is a per-language display name shown in the viewer header.
 *
 * DO NOT hard-code English titles here — use the i18n keys defined
 * in `frontend/src/locales/*.json` under `docs.<id>.title`.
 */
import tapProtectionEn from '../docs/tapProtection.en.js';
import tapProtectionFr from '../docs/tapProtection.fr.js';
import tapProtectionEs from '../docs/tapProtection.es.js';
import loyalReviewEn from '../docs/loyalReview.en.js';
import loyalReviewFr from '../docs/loyalReview.fr.js';
import loyalReviewEs from '../docs/loyalReview.es.js';


/**
 * @typedef DocEntry
 * @property {string} id           stable slug, e.g. "tapProtection"
 * @property {string} titleKey     i18n key resolving to the title, e.g. "docs.tapProtection.title"
 * @property {Object<string,string>} content  map of language code -> markdown string
 * @property {string=} category    grouping key for a future docs library page
 */

/** @type {DocEntry[]} */
export const DOCS = [
    {
        id: 'tapProtection',
        titleKey: 'docs.tapProtection.title',
        category: 'security',
        content: {
            en: tapProtectionEn,
            fr: tapProtectionFr,
            es: tapProtectionEs,
        },
    },
    {
        id: 'loyalReview',
        titleKey: 'docs.loyalReview.title',
        category: 'reviewShield',
        content: {
            en: loyalReviewEn,
            fr: loyalReviewFr,
            es: loyalReviewEs,
        },
    },
    // Future entries:
    //   { id: 'surveyIntelligence', ... }
    //   { id: 'reviewShield', ... }
    //   { id: 'communicationsHub', ... }
    //   ...
];


/** Look up a doc by id. Returns null if the id is not registered. */
export function getDoc(id) {
    return DOCS.find((d) => d.id === id) || null;
}


/**
 * Resolve the markdown body for a doc in the current UI language,
 * falling back to English if the requested language is missing.
 */
export function resolveDocContent(id, lang) {
    const doc = getDoc(id);
    if (!doc) return null;
    return doc.content[lang] || doc.content.en || null;
}
